import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Smartphone, Loader2, Save, AlertTriangle, Apple } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A version is a dotted numeric string. Anything else is rejected before saving:
// this single field can lock every user of a platform out of the app, so a typo
// like "v2.1" or "2.x" must never reach the database.
const VERSION_RE = /^\d+(\.\d+){0,3}$/;

const FIELDS = {
  iosVersion: "infinity_app_min_version_ios",
  androidVersion: "infinity_app_min_version_android",
  iosUrl: "infinity_app_store_url_ios",
  androidUrl: "infinity_app_store_url_android",
  message: "infinity_app_update_message",
};

/**
 * Super-admin control for the Infinity Touch app's force-update wall.
 *
 * A phone running a version BELOW the cut-off shows a blocking popup, is signed
 * out, and cannot use the app until it updates. Leaving a cut-off blank turns the
 * wall off for that platform — which is the shipped default, so this feature does
 * nothing at all until someone deliberately fills it in.
 *
 * iOS and Android are set separately on purpose: App Store and Play Store
 * approvals land on different days, and one shared number would force you either
 * to lock out a platform whose build is not live yet, or to leave the other one
 * unprotected while you wait.
 */
export default function AdminInfinityAppVersion() {
  const [form, setForm] = useState({
    iosVersion: "",
    androidVersion: "",
    iosUrl: "",
    androidUrl: "",
    message: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/platform-settings`, authHeaders());
        setForm({
          iosVersion: data?.[FIELDS.iosVersion] || "",
          androidVersion: data?.[FIELDS.androidVersion] || "",
          iosUrl: data?.[FIELDS.iosUrl] || "",
          androidUrl: data?.[FIELDS.androidUrl] || "",
          message: data?.[FIELDS.message] || "",
        });
      } catch {
        toast.error("Failed to load the app version settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const save = async () => {
    const ios = form.iosVersion.trim();
    const android = form.androidVersion.trim();

    for (const [label, value] of [["iOS", ios], ["Android", android]]) {
      if (value && !VERSION_RE.test(value)) {
        toast.error(`${label} minimum version must look like 2.1.0 — "${value}" is not a version number`);
        return;
      }
    }
    if (ios && !form.iosUrl.trim()) {
      toast.error("Set the App Store link before switching the iOS wall on, or the update button has nowhere to go");
      return;
    }
    if (android && !form.androidUrl.trim()) {
      toast.error("Set the Play Store link before switching the Android wall on, or the update button has nowhere to go");
      return;
    }

    setSaving(true);
    try {
      // Partial PUT — only this page's keys. The platform settings document also
      // holds Shopify, Stripe, ShipStation and email config owned by the main
      // Settings page; sending the whole object back would risk clobbering it.
      await axios.put(
        `${API}/admin/platform-settings`,
        {
          [FIELDS.iosVersion]: ios,
          [FIELDS.androidVersion]: android,
          [FIELDS.iosUrl]: form.iosUrl.trim(),
          [FIELDS.androidUrl]: form.androidUrl.trim(),
          [FIELDS.message]: form.message.trim(),
        },
        authHeaders()
      );
      toast.success("App version settings saved");
      setHasChanges(false);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const anyWallOn = Boolean(form.iosVersion.trim() || form.androidVersion.trim());

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Smartphone className="w-6 h-6" />
            App Version
            {hasChanges ? <Badge variant="secondary" className="ml-2">Unsaved changes</Badge> : null}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Force funeral homes onto a newer build of the Infinity Touch mobile app.
          </p>
        </div>
        <Button onClick={save} disabled={saving || !hasChanges} className="h-11">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
      </div>

      <div
        className={`rounded-xl p-4 text-sm flex gap-3 ${
          anyWallOn ? "bg-amber-50 border border-amber-200" : "bg-slate-50"
        }`}
      >
        <AlertTriangle className={`w-5 h-5 shrink-0 ${anyWallOn ? "text-amber-600" : "text-slate-400"}`} />
        <div>
          {anyWallOn ? (
            <p className="text-amber-900 font-medium">
              The update wall is ON. Anyone running an older build is signed out and cannot use the app
              until they update.
            </p>
          ) : (
            <p className="text-slate-700 font-medium">The update wall is off.</p>
          )}
          <p className="text-slate-500 mt-1">
            Leave a minimum version <strong>blank</strong> to turn the wall off for that platform. Only
            builds <em>below</em> the number are blocked — set it to the version you have just published,
            never to something newer than what is live in the store.
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="bg-white border rounded-2xl p-6 space-y-5">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <Apple className="w-4 h-4" /> iOS
          </h2>
          <div className="space-y-2">
            <Label htmlFor="ios-version">Minimum version</Label>
            <Input
              id="ios-version"
              value={form.iosVersion}
              onChange={(e) => onChange("iosVersion", e.target.value)}
              placeholder="e.g. 2.1.0 — blank = wall off"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ios-url">App Store link</Label>
            <Input
              id="ios-url"
              value={form.iosUrl}
              onChange={(e) => onChange("iosUrl", e.target.value)}
              placeholder="https://apps.apple.com/app/id..."
            />
            <p className="text-xs text-slate-500">
              Needs the numeric App Store ID from App Store Connect. The update button is hidden while
              this is blank.
            </p>
          </div>
        </div>

        <div className="bg-white border rounded-2xl p-6 space-y-5">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <Smartphone className="w-4 h-4" /> Android
          </h2>
          <div className="space-y-2">
            <Label htmlFor="android-version">Minimum version</Label>
            <Input
              id="android-version"
              value={form.androidVersion}
              onChange={(e) => onChange("androidVersion", e.target.value)}
              placeholder="e.g. 2.1.0 — blank = wall off"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="android-url">Play Store link</Label>
            <Input
              id="android-url"
              value={form.androidUrl}
              onChange={(e) => onChange("androidUrl", e.target.value)}
              placeholder="https://play.google.com/store/apps/details?id=..."
            />
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-2xl p-6 space-y-3 max-w-3xl">
        <Label htmlFor="update-message">Message shown in the app (optional)</Label>
        <Textarea
          id="update-message"
          rows={3}
          value={form.message}
          onChange={(e) => onChange("message", e.target.value)}
          placeholder="Leave blank to use the app's own wording."
        />
        <p className="text-xs text-slate-500">
          Shown on the blocking popup for both platforms. Keep it short — it sits under a
          &ldquo;Update required&rdquo; heading.
        </p>
      </div>
    </div>
  );
}
